import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';

/**
 * Modal step shown to the user. Local view state derived from `pendingSetup.step`.
 *
 * Same three-step shape as the Topal connect modal: welcome (form) → testing
 * (spinner while the BE logs in once, reads the Fibu master data and logs out again)
 * → error. No `created` step: on success the manage modal takes over and this modal
 * closes.
 *
 * The reducer uses `awaiting_consent` for the "BE is testing the credentials" phase
 * (a shared string across vendors); we map it to the local `'testing'` step here.
 */
export type ProffixModalViewStep = 'welcome' | 'testing' | 'error';

/**
 * Origin (`https://host[:port]`) optionally followed by `/pxapi/V4` and a trailing slash.
 * Mirrors the BE's `normalizeProffixServerUrl` (REV-22536 D2) — https only (SRR F16: the login and
 * the session id are password-equivalent and never travel over plain http).
 */
export const PROFFIX_SERVER_URL_PATTERN = /^https:\/\/[^/\s]+(\/pxapi\/v4)?\/?$/i;

@Component({
    selector: 'aumico-proffix-connect-modal',
    templateUrl: './proffix-connect-modal.component.html',
    styleUrls: ['./proffix-connect-modal.component.scss'],
})
export class ProffixConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: ProffixModalViewStep = 'welcome';
    public errorMessage: string | null = null;

    /** Form values: local to the modal until submitted. NOT in NgRx until dispatch. */
    public displayName = '';
    public serverUrl = '';
    public restApiKey = '';
    public username = '';
    public password = '';
    /** Toggle the masking of the two secret fields (`type=password` ↔ `type=text`). */
    public showRestApiKey = false;
    public showPassword = false;

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.PROFFIX,
    );

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                this.errorMessage = setup.error;

                if (setup.vendor !== IntegrationVendor.PROFFIX) return;

                // On success: the manage modal takes over. Close this immediately.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * All five fields required. Display name distinguishes connections in the manage
     * modal's server list. Server URL must be an origin (a port is allowed) optionally
     * followed by `/pxapi/V4` (with or without trailing slash) — any other path is
     * rejected, the BE normalizes to the origin. The rest are non-empty strings.
     */
    public get isFormValid(): boolean {
        return this.missingFieldLabelKeys.length === 0 && !this.serverUrlInvalid;
    }

    /**
     * A typed server URL the pattern rejects (e.g. `…/pxapi/V4/PRO/Info` copied from the Proffix docs).
     * Shown inline under the field so the disabled Connect says why (guidelines §2.1). An empty field is
     * not invalid but missing — that is `missingFieldLabelKeys`.
     */
    public get serverUrlInvalid(): boolean {
        const url = this.serverUrl.trim();
        return url.length > 0 && !PROFFIX_SERVER_URL_PATTERN.test(url);
    }

    /** Label keys of the required fields still empty, in form order — named next to the Connect button. */
    public get missingFieldLabelKeys(): string[] {
        const fields: [boolean, string][] = [
            [this.displayName.trim().length > 0, 'aumico_integration_setup_displayname_label'],
            [this.serverUrl.trim().length > 0, 'aumico_proffix_step_config_serverurl_label'],
            [this.restApiKey.length > 0, 'aumico_proffix_step_config_restapikey_label'],
            [this.username.trim().length > 0, 'aumico_proffix_step_config_username_label'],
            [this.password.length > 0, 'aumico_proffix_step_config_password_label'],
        ];
        return fields.filter(([filled]) => !filled).map(([, labelKey]) => labelKey);
    }

    public onSubmit(): void {
        if (!this.isFormValid) return;
        this.integrationProvider.startProffixConnect({
            displayName: this.displayName.trim(),
            serverUrl: this.serverUrl.trim(),
            restApiKey: this.restApiKey,
            username: this.username.trim(),
            password: this.password,
        });
    }

    public onToggleRestApiKey(): void {
        this.showRestApiKey = !this.showRestApiKey;
    }

    public onTogglePassword(): void {
        this.showPassword = !this.showPassword;
    }

    public onRetryFromError(): void {
        // Clear the reducer's pendingSetup slice FIRST: otherwise the next
        // `getPendingSetup$()` emission re-maps `step: 'error'` and yanks us straight
        // back to the error view. cancelSetup wipes the slice without closing the modal.
        this.integrationProvider.cancelSetup();
        this.errorMessage = null;
        this.step = 'welcome';
    }

    /**
     * Not while the credentials are tested: the POST would finish anyway and the BE would store the
     * connection, but with `pendingSetup` reset nothing picks it up, so auto-match never runs. Aborting
     * the request does not help (the BE may already have stored it). The modal waits for the answer; the
     * template offers no Cancel, close icon or Escape in that step, and this guard covers any other path.
     */
    public onCancel(): void {
        if (this.step === 'testing') return;
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): ProffixModalViewStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'testing';
            case 'error':
                return 'error';
            case 'created':
                // Handled separately by closing the modal: should never render.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
